import React from 'react';

import { Select } from '@aline/common';
import { Sparkles } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import {
  SUGGESTION_PILL_CURRENT_BORDERS,
  SUGGESTION_PILL_CURRENT_TONES,
  SUGGESTION_PILL_TONES,
} from './suggestion-tones';
import type { SuggestionPillKind } from './suggestion-tones';
import { SuggestionActions } from './SuggestionActions';
import { SuggestionRationaleTooltip } from './SuggestionRationaleTooltip';

const useStyles = makeStyles<{
  centered: boolean;
  current: boolean;
  dashed: boolean;
  kind: SuggestionPillKind;
}>()((theme, { centered, current, dashed, kind }) => {
  const tone = current ? SUGGESTION_PILL_CURRENT_TONES[kind] : SUGGESTION_PILL_TONES[kind];
  const borderStyle = dashed ? 'dashed' : 'solid';
  const borderColor = current ? SUGGESTION_PILL_CURRENT_BORDERS[kind] : tone.border;

  return {
    copy: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    // The space to the ✕ on its right matches the pill's padding after the ✓.
    divider: {
      alignSelf: 'stretch',
      backgroundColor: tone.border,
      flexShrink: 0,
      margin: theme.spacing(0.5, 0.5, 0.5, 0.75),
      width: 1,
    },
    icon: {
      flexShrink: 0,
      marginRight: theme.spacing(0.75),
    },
    pill: {
      alignItems: 'center',
      backgroundColor: tone.background,
      border: `1px ${borderStyle} ${borderColor}`,
      borderRadius: 6,
      color: tone.color,
      cursor: 'pointer',
      display: centered ? 'flex' : 'inline-flex',
      fontSize: 13,
      fontWeight: 500,
      justifyContent: centered ? 'center' : undefined,
      maxWidth: '100%',
      minWidth: 0,
      /**
       * The current stop's border is doubled by an outline in the same colour,
       * so it reads thicker without taking layout space, like a current card.
       */
      outline: current ? `1px ${borderStyle} ${borderColor}` : undefined,
      /**
       * No vertical padding: the 26px icon buttons plus the border make the
       * pill 28px, so it lines up with a 28px label beside it.
       */
      padding: theme.spacing(0, 0.5, 0, 1.25),
    },
  };
});

interface SuggestionPillProps {
  acceptDisabledReason: string | null;
  kind: SuggestionPillKind;
  label: string;
  onAccept: () => void;
  onReject: () => void;
  /** Clicking the copy, e.g. to make the suggestion the current stop. */
  onSelect: () => void;
  /** Why the AI proposed this, shown over the copy. */
  rationale: string;
  acceptTitle?: string;
  /** Fills its container with the copy and actions centred, for a pill that stands on its own row. */
  centered?: boolean;
  className?: string;
  /** The pill's suggestion is the current stop of the suggestions bar. */
  current?: boolean;
  /** A dashed outline, for a pill that marks where something would land. */
  dashed?: boolean;
  rejectTitle?: string;
}

/**
 * A suggestion named in full with its ✕ / ✓ behind a divider, in the kind
 * colour. The larger sibling of `SuggestionChip`, which is the label alone.
 */
export const SuggestionPill = React.memo(function SuggestionPill({
  acceptDisabledReason,
  acceptTitle,
  centered = false,
  className,
  current = false,
  dashed = false,
  kind,
  label,
  onAccept,
  onReject,
  onSelect,
  rationale,
  rejectTitle,
}: SuggestionPillProps): JSX.Element {
  const { classes, cx } = useStyles({ centered, current, dashed, kind });

  return (
    <div
      className={cx(classes.pill, className)}
      data-cy={Select.playbooks.suggestionPill}
      data-kind={kind}
      onClick={onSelect}
    >
      <Sparkles className={classes.icon} size={12} strokeWidth={1.5} />
      <SuggestionRationaleTooltip rationales={[rationale]}>
        <span className={classes.copy}>{label}</span>
      </SuggestionRationaleTooltip>
      <span className={classes.divider} />
      <SuggestionActions
        acceptDisabledReason={acceptDisabledReason}
        acceptTitle={acceptTitle}
        rejectTitle={rejectTitle}
        size={16}
        onAccept={onAccept}
        onReject={onReject}
      />
    </div>
  );
});
